import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { AccountWorksheetDrawer } from "@/components/accounting/AccountWorksheetDrawer";
import { useLedger } from "@/hooks/useLedger";
import type { LedgerAccount, LedgerAccountInput, LedgerJournal } from "@/types/ledger";
import { useToast } from "@/components/ToastProvider";
import { SectionError, SkeletonRows } from "@/components/ui/error-ui";

const ACCOUNT_TYPES: LedgerAccountInput["type"][] = ["asset", "liability", "equity", "income", "expense"];
const DEFAULT_CURRENCY = "USD";

type EditableFields = {
  code: string;
  name: string;
  type: LedgerAccount["type"];
  currency: string;
  is_active: boolean;
};

type EditableRow = {
  original: LedgerAccount;
  draft: EditableFields;
};

function createEditableRow(account: LedgerAccount): EditableRow {
  return {
    original: account,
    draft: {
      code: account.code,
      name: account.name,
      type: account.type,
      currency: account.currency,
      is_active: account.is_active,
    },
  };
}

export function AccountsEditorPanel() {
  const {
    accounts,
    createAccount,
    updateAccount,
    refresh,
    loading,
    error,
    hasArtist,
    journals,
    createEntry,
  } = useLedger();
  const { push } = useToast();
  const [rows, setRows] = useState<Record<number, EditableRow>>({});
  const [savingId, setSavingId] = useState<number | null>(null);
  const [code, setCode] = useState("1100");
  const [name, setName] = useState("");
  const [type, setType] = useState<LedgerAccountInput["type"]>("asset");
  const [currency, setCurrency] = useState(DEFAULT_CURRENCY);
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [worksheetAccount, setWorksheetAccount] = useState<LedgerAccount | null>(null);

  useEffect(() => {
    const nextRows: Record<number, EditableRow> = {};
    accounts.forEach((account) => {
      nextRows[account.id] = createEditableRow(account);
    });
    setRows(nextRows);
  }, [accounts]);

  const sortedRows = useMemo(
    () => Object.values(rows).sort((a, b) => a.draft.code.localeCompare(b.draft.code)),
    [rows],
  );

  const onFieldChange = <K extends keyof EditableFields>(accountId: number, field: K, value: EditableFields[K]) => {
    setRows((prev) => {
      const target = prev[accountId];
      if (!target) return prev;
      return {
        ...prev,
        [accountId]: {
          ...target,
          draft: {
            ...target.draft,
            [field]: value,
          },
        },
      };
    });
  };

  const resetRow = (accountId: number) => {
    setRows((prev) => {
      const target = prev[accountId];
      if (!target) return prev;
      return {
        ...prev,
        [accountId]: createEditableRow(target.original),
      };
    });
  };

  const diffRow = (row: EditableRow) => {
    const diff: Partial<EditableFields> = {};
    (Object.keys(row.draft) as Array<keyof EditableFields>).forEach((field) => {
      if (row.draft[field] !== row.original[field]) {
        diff[field] = row.draft[field];
      }
    });
    return diff;
  };

  const hasChanges = (row: EditableRow) => Object.keys(diffRow(row)).length > 0;

  const handleSaveRow = async (row: EditableRow) => {
    const changes = diffRow(row);
    if (!Object.keys(changes).length) {
      push({ title: "No changes to save", message: `${row.original.code} is already up to date.` });
      return;
    }
    setSavingId(row.original.id);
    try {
      await updateAccount(row.original.id, {
        ...changes,
        currency: changes.currency?.toUpperCase(),
      });
      push({ title: "Account updated", message: `${row.original.code} saved.` });
    } catch (err) {
      push({
        title: "Update failed",
        message: err instanceof Error ? err.message : "Unable to update account",
      });
    } finally {
      setSavingId(null);
    }
  };

  const handleCreateAccount = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!code.trim() || !name.trim()) {
      setFormError("Code and name are required");
      return;
    }
    setSubmitting(true);
    setFormError(null);
    try {
      await createAccount({
        code: code.trim(),
        name: name.trim(),
        type,
        currency: currency.trim().toUpperCase() || DEFAULT_CURRENCY,
      });
      push({ title: "Account created", message: `${code.trim()} · ${name.trim()}` });
      setCode("1100");
      setName("");
      setType("asset");
      setCurrency(DEFAULT_CURRENCY);
    } catch (err) {
      setFormError(err instanceof Error ? err.message : "Unable to create account");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6 rounded-2xl border border-border bg-background/70 p-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold text-primary-foreground dark:text-amber-100">Accounts worksheet</h2>
          <p className="text-sm text-muted-foreground">
            Spreadsheet-style editor for the full chart of accounts. Click a field to edit, then save inline.
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <Button variant="ghost" asChild>
            <Link to="/accounting">Open accounting console</Link>
          </Button>
          <Button variant="outline" onClick={() => refresh()} disabled={loading}>
            Refresh
          </Button>
        </div>
      </div>

      {!hasArtist && (
        <div className="rounded-2xl border border-warning/40 bg-warning/10 px-4 py-3 text-sm text-warning">
          Select an artist profile in the header to load their accounts.
        </div>
      )}

      {error && hasArtist && <SectionError message={error} onRetry={() => void refresh()} />}

      {hasArtist && (
        <div className="space-y-6">
          <div className="overflow-x-auto rounded-2xl border border-border bg-background/30">
            <table className="min-w-full text-sm">
              <thead className="bg-background/60 text-[11px] uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="p-3 text-left">Code</th>
                <th className="p-3 text-left">Name</th>
                <th className="p-3 text-left">Type</th>
                <th className="p-3 text-left">Currency</th>
                <th className="p-3 text-left">Balance</th>
                <th className="p-3 text-left">Status</th>
                <th className="p-3 text-right">Actions</th>
              </tr>
              </thead>
              <tbody>
                {loading && sortedRows.length === 0 && (
                  <tr><td colSpan={7}><SkeletonRows rows={5} /></td></tr>
                )}
                {sortedRows.map((row) => (
                <tr key={row.original.id} className="border-t border-border/70">
                    <td className="p-2 align-middle">
                      <input
                        value={row.draft.code}
                        onChange={(event) => onFieldChange(row.original.id, "code", event.target.value)}
                        className="w-full rounded border border-border bg-transparent px-2 py-1 font-mono text-xs text-foreground focus:border-primary"
                      />
                    </td>
                    <td className="p-2 align-middle">
                      <input
                        value={row.draft.name}
                        onChange={(event) => onFieldChange(row.original.id, "name", event.target.value)}
                        className="w-full rounded border border-border bg-transparent px-2 py-1 text-foreground focus:border-primary"
                      />
                    </td>
                  <td className="p-2 align-middle">
                    <select
                      value={row.draft.type}
                      onChange={(event) => onFieldChange(row.original.id, "type", event.target.value as LedgerAccount["type"])}
                      className="w-full rounded border border-border bg-transparent px-2 py-1 text-foreground focus:border-primary"
                    >
                      {ACCOUNT_TYPES.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="p-2 align-middle">
                    <input
                      value={row.draft.currency}
                      onChange={(event) => onFieldChange(row.original.id, "currency", event.target.value.toUpperCase())}
                      maxLength={3}
                      className="w-full rounded border border-border bg-transparent px-2 py-1 text-center text-foreground focus:border-primary"
                    />
                  </td>
                  <td className="p-2 align-middle text-sm text-foreground/70">
                    {Number(row.original.balance ?? 0).toLocaleString(undefined, { style: "currency", currency: "USD" })}
                  </td>
                  <td className="p-2 align-middle">
                    <div className="flex gap-3 text-xs text-foreground/80">
                        <label className="inline-flex items-center gap-1">
                          <input
                            type="radio"
                            name={`status-${row.original.id}`}
                            checked={row.draft.is_active}
                            onChange={() => onFieldChange(row.original.id, "is_active", true)}
                          />
                          Active
                        </label>
                        <label className="inline-flex items-center gap-1">
                          <input
                            type="radio"
                            name={`status-${row.original.id}`}
                            checked={!row.draft.is_active}
                            onChange={() => onFieldChange(row.original.id, "is_active", false)}
                          />
                          Inactive
                        </label>
                      </div>
                    </td>
                  <td className="p-2 text-right align-middle">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        size="sm"
                        variant="secondary"
                        disabled={!hasChanges(row) || savingId === row.original.id}
                        onClick={() => void handleSaveRow(row)}
                      >
                        {savingId === row.original.id ? "Saving…" : "Save"}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={!hasChanges(row) || savingId === row.original.id}
                        onClick={() => resetRow(row.original.id)}
                      >
                        Reset
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setWorksheetAccount(row.original)}>
                        Worksheet
                      </Button>
                    </div>
                  </td>
                </tr>
                ))}
                {sortedRows.length === 0 && (
                  <tr>
                    <td colSpan={6} className="p-4 text-center text-xs text-muted-foreground">
                      No accounts yet. Use the form below to add the first account.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <form
            onSubmit={handleCreateAccount}
            className="rounded-2xl border border-border bg-background/40 p-5 text-sm shadow-inner shadow-black/30"
          >
            <div className="mb-3 text-[11px] uppercase tracking-wide text-muted-foreground">Add account</div>
            {formError && (
              <div className="mb-3 rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-destructive">
                {formError}
              </div>
            )}
            <div className="grid gap-4 md:grid-cols-2">
              <label className="text-foreground/70">
                <span className="mb-1 block text-[11px] uppercase tracking-wide text-muted-foreground">Code</span>
                <input
                  type="text"
                  value={code}
                  onChange={(event) => setCode(event.target.value)}
                  className="w-full rounded-lg border border-border bg-background px-3 py-2 text-foreground"
                />
              </label>
              <label className="text-foreground/70">
                <span className="mb-1 block text-[11px] uppercase tracking-wide text-muted-foreground">Name</span>
                <input
                  type="text"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  className="w-full rounded-lg border border-border bg-background px-3 py-2 text-foreground"
                  placeholder="Cash on hand"
                />
              </label>
              <label className="text-foreground/70">
                <span className="mb-1 block text-[11px] uppercase tracking-wide text-muted-foreground">Type</span>
                <select
                  value={type}
                  onChange={(event) => setType(event.target.value as LedgerAccountInput["type"])}
                  className="w-full rounded-lg border border-border bg-background px-3 py-2 text-foreground"
                >
                  {ACCOUNT_TYPES.map((entry) => (
                    <option key={entry} value={entry}>
                      {entry}
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-foreground/70">
                <span className="mb-1 block text-[11px] uppercase tracking-wide text-muted-foreground">Currency</span>
                <input
                  type="text"
                  value={currency}
                  onChange={(event) => setCurrency(event.target.value.toUpperCase())}
                  maxLength={3}
                  className="w-full rounded-lg border border-border bg-background px-3 py-2 text-foreground"
                />
              </label>
            </div>
            <div className="mt-4 flex justify-end gap-3">
              <Button type="submit" disabled={submitting}>
                {submitting ? "Saving…" : "Add account"}
              </Button>
            </div>
          </form>
        </div>
      )}
      <AccountWorksheetDrawer
        account={worksheetAccount}
        open={Boolean(worksheetAccount)}
        onClose={() => setWorksheetAccount(null)}
        accounts={accounts}
        journals={journals as LedgerJournal[]}
        onUpdated={refresh}
        createEntry={createEntry}
      />
    </div>
  );
}
